import React from "react";
import { Link } from "react-router-dom";
import { colors, fonts } from "../theme";
import { categories } from "../data";
import { Eyebrow, Btn, CategoryCard, SectionHead } from "../ui";
import Seo from "../../seo/Seo";
import { PAGE_META } from "../../seo/pageMeta";
import { hasProductPage, productPath } from "../../seo/productMeta";
import { webPageSchema, breadcrumbSchema, itemListSchema } from "../../seo/schema";

const wrap = { maxWidth: "1180px", margin: "0 auto" };

// Every grade that has a real product page, grouped under its category.
//
// This exists for crawling, not for navigation — the category cards above
// already cover the browsing case. On 8 Aug 2026 Search Console reported all
// 21 grade pages unindexed: 14 "Discovered - currently not indexed" (Google
// knew the URL and never spent a crawl on it) and 7 "Crawled - currently not
// indexed". The only internal links into a grade page were from its own
// category page and from its siblings, which puts every grade three clicks
// from the home page behind pages that are themselves weakly linked. That link
// profile is what Google reads as "not worth the crawl budget".
//
// Listing them here puts all 21 one hop from /catalogue, which is the second
// most internally-linked page on the site, and one hop further from the home
// page and the footer. It also gives /catalogue something to be about: at 331
// rendered words it was the thinnest page on the site, a heading over six
// cards.
const GRADES = categories
  .map((cat) => ({
    cat,
    items: (cat.items || []).filter((item) => hasProductPage(cat.slug, item.slug)),
  }))
  .filter((group) => group.items.length > 0);

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Catalogue", path: "/catalogue" },
];

// The grid starts roughly 340px down the page — directly under the h1 and one
// paragraph — so the first row is above the fold on every viewport and holds
// the LCP candidate. A 260px track floor in a 1124px column gives four cards
// per desktop row; those load eagerly, the rest stay lazy.
//
// This stays a constant rather than a viewport measurement: the site is
// prerendered at build time, so anything derived from `window` here would be
// frozen into the static HTML at whatever size the build machine reported.
const EAGER_CARDS = 4;

export default function Catalogue() {
  return (
    <main style={{ ...wrap, padding: "clamp(36px,8vw,60px) var(--sp-gutter) var(--sp-section-bot)" }}>
      <Seo
        title={PAGE_META.catalogue.title}
        description={PAGE_META.catalogue.description}
        path="/catalogue"
        schema={[
          webPageSchema({
            path: "/catalogue",
            name: PAGE_META.catalogue.title,
            description: PAGE_META.catalogue.description,
            type: "CollectionPage",
            breadcrumbId: "https://sigmapapermart.com/catalogue#breadcrumb",
          }),
          breadcrumbSchema(CRUMBS, "/catalogue"),
          itemListSchema({
            path: "/catalogue",
            name: "Paper & board categories",
            items: categories.map((c) => ({
              name: c.name,
              path: `/catalogue/${c.slug}`,
              desc: c.blurb,
              image: c.image,
            })),
          }),
          // A second list, of the individual grades. The categories list above
          // describes how the page is organised; this one enumerates the 21
          // destinations, which is what a crawler needs when the problem is
          // that those destinations were never crawled.
          itemListSchema({
            path: "/catalogue",
            anchor: "grades",
            name: "Paper & board grades",
            items: GRADES.flatMap(({ cat, items }) =>
              items.map((item) => ({
                name: item.name,
                path: productPath(cat.slug, item.slug),
              }))
            ),
          }),
        ]}
      />

      <Eyebrow>Product Catalogue</Eyebrow>
      <h1
        style={{
          fontFamily: fonts.serif,
          fontSize: "clamp(30px,6vw,46px)",
          fontWeight: 500,
          margin: "12px 0 10px",
          maxWidth: "20ch",
        }}
      >
        Our full range of paper &amp; board
      </h1>
      {/* No lineHeight was set at all, so this three-line paragraph inherited
          the body default and ran under the 1.5 minimum. */}
      <p style={{ fontSize: "var(--fs-body)", lineHeight: 1.6, color: colors.muted, maxWidth: "60ch", margin: "0 0 40px" }}>
        A wide range across coated, uncoated, packaging and specialty stock — 20+ product lines organised into{" "}
        {categories.length} categories. Choose a category to see its grades, or enquire for exact specifications and
        pricing.
      </p>

      {/* min(260px,100%) keeps the track floor inside the container — a bare
          260px minimum pushes the page sideways once the column is narrower. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(260px,100%),1fr))", gap: "18px" }}>
        {categories.map((cat, i) => (
          <CategoryCard
            key={cat.slug}
            cat={cat}
            to={`/catalogue/${cat.slug}`}
            priority={i < EAGER_CARDS}
          />
        ))}
      </div>

      {/* Every grade, by category. See the note on GRADES above for why this
          is here: the category cards are the browsing path, this is the crawl
          path, and it is the only place on the site that links all 21 grade
          pages from one document. */}
      <section id="grades" style={{ marginTop: "clamp(48px,9vw,72px)" }}>
        <SectionHead
          eyebrow="Every grade we stock"
          title="Paper and board grades, A to Z"
        />
        <p
          style={{
            fontSize: "var(--fs-body)",
            lineHeight: 1.6,
            color: colors.muted,
            maxWidth: "60ch",
            margin: "14px 0 32px",
          }}
        >
          Each grade has its own page with grammages, typical applications and
          the questions buyers usually ask. Tell us the size and quantity and we
          will quote from stock.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(min(260px,100%),1fr))",
            gap: "clamp(20px,4vw,32px)",
          }}
        >
          {GRADES.map(({ cat, items }) => (
            <div key={cat.slug}>
              {/* The category name is a heading and a link both. A bare label
                  would make this the one listing on the site that names a
                  category without pointing at it. */}
              <h3 style={{ fontSize: "var(--fs-body)", fontWeight: 600, margin: "0 0 12px" }}>
                <Link to={`/catalogue/${cat.slug}`} style={{ color: colors.ink }}>
                  {cat.name}
                </Link>
              </h3>
              {/* se-flinks is the footer's link-column rule: below 860px it
                  blocks each anchor out to a 44px tap target and zeroes the
                  gap this would otherwise double. Same job here. */}
              <ul
                className="se-flinks"
                style={{
                  listStyle: "none",
                  margin: 0,
                  padding: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  fontSize: "var(--fs-base)",
                  color: colors.muted,
                }}
              >
                {items.map((item) => (
                  <li key={item.slug}>
                    <Link to={productPath(cat.slug, item.slug)} style={{ cursor: "pointer" }}>
                      {item.name}
                      {item.tag ? (
                        <span style={{ color: colors.muted2, fontFamily: fonts.mono, fontSize: "var(--fs-meta)" }}>
                          {" "}
                          · {item.tag}
                        </span>
                      ) : null}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* se-panel trims the nested padding below 560px, where 32px each side
          sat inside the page gutter and cost the copy a third of its width;
          se-cta-band stretches the button to full width in the same range. */}
      <div
        className="se-cta-band se-panel"
        style={{
          marginTop: "56px",
          padding: "34px 32px",
          border: `1px solid ${colors.border}`,
          borderRadius: "4px",
          background: colors.panel,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "24px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontSize: "16px", color: colors.ink3, maxWidth: "52ch" }}>
          Can't find a grade? We source a wider range on request — tell us your requirement.
        </div>
        {/* The padding override is gone on purpose: 13px on a 15px line box
            measured 42.9px, under the 44px floor Btn now holds by default. */}
        <Btn variant="dark" to="/contact" style={{ whiteSpace: "nowrap" }}>
          Enquire now →
        </Btn>
      </div>
    </main>
  );
}
